import React, { Suspense, useState } from 'react';
import {
  SubmitHandler,
  Controller,
  useForm,
  SubmitErrorHandler,
} from 'react-hook-form';
import {
  IUserResetPasswordReq,
  IUserResetPasswordRes,
} from '@models/interfaces/user.interface';
import { Button, Input, Checkbox } from 'geniebook-ui/dist';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AxiosResponse } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import userService from '@services/GeniebookAPI/UserServices.services';
import './LoginForm.css';

//validation login
const schema = yup
  .object({
    new_password: yup
      .string()
      .required('New password is required')
      .min(8, 'Password must be at least 8 characters.')
      .typeError('Password is invalid'),
    confirm_password: yup
      .string()
      .required('Confirm password is required')
      .min(8, 'Password must be at least 8 characters.')
      .when('new_password', (new_password, schema) => 
        new_password ? schema.oneOf([yup.ref('new_password')], "Passwords do not match.") : schema
      )
      .typeError('Confirm Password is invalid'),
  })
  .required();

export default function ResetPasswordCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    error: false,
    error_msg: '',
    error_code: '',
  });
  const { token } = useParams();
  const [successMsg, setSuccessMsg] = useState('');
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(false);

  const initialValues: IUserResetPasswordReq = {
    new_password: '',
    confirm_password: '',
    reset_token: token || '',
    logout_other_devices: 0,
  };

  const handleShowPassword = (whichPassword: string) => {
    if (whichPassword === 'new') {
      setIsShowPassword(!isShowPassword);
    } else if (whichPassword === 'confirm') {
      setIsShowConfirmPassword(!isShowConfirmPassword);
    }
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
  } = useForm<IUserResetPasswordReq>({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const [forceErrorMessage, setForceErrorMessage] = useState<string>('');

  const handleLogOutOnChange = () => {
    let val: number = getValues('logout_other_devices');
    if (val === 0) val = 1;
    else val = 0;

    setValue('logout_other_devices', val);
    setIsLoggedOut(!isLoggedOut);
  };

  const onInvalidSubmit: SubmitErrorHandler<IUserResetPasswordReq> = data => {
    if (data.confirm_password?.message === 'Passwords do not match.') {
      setForceErrorMessage('Passwords do not match.');
    }
  };

  const onSubmit: SubmitHandler<IUserResetPasswordReq> = async data => {
    setForceErrorMessage('');
    setLoading(true);
    setError({
      error: false,
      error_msg: '',
      error_code: '',
    });
    try {
      const res: AxiosResponse = await userService.resetPassword(data);
      const body: IUserResetPasswordRes = res.data;
      if (body.error) {
        setError({
          ...error,
          error: body.error,
          error_msg: body.error_msg,
          error_code: body.error_code,
        });
      } else {
        setSuccessMsg(body.msg || '');
      }
    } catch (error) {
      setError({
        error: true,
        error_msg: 'Unknown Error',
        error_code: '',
      });
    } finally {
      setLoading(false);
    }
  };

  return successMsg ? (
    <>
      <div className="font-black text-primary-navyblue text-[32px] leading-[34px] mb-[20px]">
        Password Updated!
      </div>
      <div className="text-[16px] leading-[24px] text-neutral-pencil mb-[32px]">
        Your password has been successfully reset. You can now login with your
        new password.
      </div>
      <Button
        className="!max-w-none w-full mb-[19px]"
        variant="primary"
        onClick={() => navigate('/')}
      >
        Return to Login
      </Button>
    </>
  ) : (
    <>
      <div className="font-black text-primary-navyblue text-[32px] leading-[34px] mb-[24px]">
        Reset Password
      </div>
      <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
        <div className="mb-4">
          <div className="relative float-right">
            <div className="absolute top-[36px] right-4">
              <div
                className={`${
                  errors.new_password
                    ? 'text-alert-error'
                    : 'text-primary-apricot'
                } hover:text-primary-skyblue text-bold`}
                onClick={() => handleShowPassword('new')}
              >
                Show
              </div>
            </div>
          </div>
          <Controller
            name="new_password"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                mode={'light'}
                placeholder="Type Password"
                className="w-full input-password"
                label="New Password"
                type={isShowPassword ? 'text' : 'password'}
                error={
                  errors.new_password
                    ? errors.new_password.message
                    : error.error
                    ? error.error_msg
                    : ''
                }
              />
            )}
          />
        </div>
        <div className="mb-4">
          <div className="relative float-right">
            <div className="absolute top-[36px] right-4">
              <div
                className={`${
                  errors.confirm_password
                    ? 'text-alert-error'
                    : 'text-primary-apricot'
                } hover:text-primary-skyblue text-bold`}
                onClick={() => handleShowPassword('confirm')}
              >
                Show
              </div>
            </div>
          </div>
          <Controller
            name="confirm_password"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                mode={'light'}
                placeholder="Type Confirm Password"
                className="w-full input-password"
                label="Confirm Password"
                type={isShowConfirmPassword ? 'text' : 'password'}
                error={
                  forceErrorMessage !== ''
                    ? forceErrorMessage
                    : errors.confirm_password
                    ? errors.confirm_password.message
                    : ''
                }
              />
            )}
          />
        </div>
        <Checkbox
          id="logout-all-device"
          {...register('logout_other_devices')}
          variant={'light'}
          isChecked={isLoggedOut}
          isValidation={false}
          onChange={handleLogOutOnChange}
          label={'Log out from all devices'}
        />
        <Suspense>
          <button className="w-full mt-[16px]">
            <Button className="!max-w-none w-full mt-4" variant="primary">
              {loading ? 'Sending...' : 'Submit'}
            </Button>
          </button>
        </Suspense>
      </form>
    </>
  );
}
